// ===========================================
// RSVP Dashboard
// Single-page form with conditional sections
// ===========================================

// Types
interface GuestRsvpChoice {
  guestId: string;
  memberIndex: number;
  memberName: string;
  attending: boolean | null; // null = not yet chosen
  starter: string;
  main: string;
  dessert: string;
  dietaryRequirements: string;
  optedOutOfChildMenu: boolean;
}

interface RsvpFormData {
  uniqueCode: string;
  emailAddress: string;
  guestChoices: GuestRsvpChoice[];
  notes: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

interface ApiResponse {
  success: boolean;
  message?: string;
  errors?: Record<string, string[]>;
}

// Breathing room left above a "#menu-selection" style target when jumping to it from an
// email link, so the section heading sits comfortably in view rather than flush to the
// top edge of the window.
const HASH_SCROLL_OFFSET = 100;

// ===========================================
// Dashboard Manager
// ===========================================
class RsvpDashboardManager {
  private form: HTMLFormElement | null = null;
  private container: HTMLElement | null = null;
  private formData: RsvpFormData;
  private isSubmitting: boolean = false;
  private canSave: boolean = false;
  private hasResponded: boolean = false;

  constructor() {
    this.formData = this.getInitialFormData();
  }

  private getInitialFormData(): RsvpFormData {
    return {
      uniqueCode: '',
      emailAddress: '',
      guestChoices: [],
      notes: ''
    };
  }

  // ===========================================
  // Initialization
  // ===========================================
  public init(): void {
    this.container = document.querySelector('.rsvp-dashboard');
    this.form = document.getElementById('rsvp-dashboard-form') as HTMLFormElement;

    if (!this.container || !this.form) return;

    // Read configuration from data attributes
    this.formData.uniqueCode = this.container.getAttribute('data-unique-code') || '';
    this.canSave = this.container.getAttribute('data-can-save') === 'true';
    this.hasResponded = this.container.getAttribute('data-has-responded') === 'true';

    // Store unique code in cookie for API calls
    if (this.formData.uniqueCode) {
      document.cookie = `code=${this.formData.uniqueCode}; path=/; SameSite=Strict`;
    }

    // Load existing data
    this.loadExistingData();

    // Bind events
    this.bindAttendanceEvents();
    this.bindInputEvents();
    this.bindFormSubmit();
    this.bindAccordionEvents();

    // Initial UI update based on existing data
    this.updateAttendingSections();
    this.updateMealAccordionVisibility();
    this.formData.guestChoices.forEach((_, index) => this.updateMealStatus(index));
    this.scrollToHashTarget();
  }

  /**
   * Handles a "#menu-selection" style link, used by the meal choice reminder email.
   * The target section starts hidden and is only revealed once attendance is known, so
   * the browser's own anchor jump has already failed by the time we get here — redo it
   * now the sections have been laid out.
   */
  private scrollToHashTarget(): void {
    const hash = window.location.hash;
    if (hash.length < 2) return;

    let target: HTMLElement | null;
    try {
      target = document.querySelector(hash);
    } catch {
      return; // Not a usable selector — ignore rather than throw during init.
    }

    // offsetParent is null while the section is still display:none, which is the case
    // for anyone who hasn't confirmed attendance yet. Nothing useful to jump to.
    if (!target || target.offsetParent === null) return;

    requestAnimationFrame(() => {
      if (!target) return;

      // Deliberately not scrollIntoView(): .rsvp-container is overflow:hidden, which still
      // counts as a scroll container, so scrollIntoView scrolls *it* rather than the page —
      // sliding the card's contents up inside a fixed box, clipped at the top with dead
      // space at the bottom. Scrolling the window explicitly can only move the page.
      const top = target.getBoundingClientRect().top + window.scrollY - HASH_SCROLL_OFFSET;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    });
  }

  private loadExistingData(): void {
    if (!this.container) return;

    // Load family members data first to build guestChoices
    const familyMembersData = this.container.getAttribute('data-family-members');
    if (familyMembersData) {
      try {
        const members = JSON.parse(familyMembersData) as Array<{
          id: string;
          index: number;
          name: string;
          attending: boolean;
          isChild: boolean;
          age: number | null;
          starter: string | null;
          main: string | null;
          dessert: string | null;
          dietaryRequirements: string | null;
          optedOutOfChildMenu: boolean | null;
        }>;
        this.formData.guestChoices = members.map(m => ({
          guestId: m.id,
          memberIndex: m.index,
          memberName: m.name,
          attending: this.hasResponded ? (m.attending ?? null) : null,
          starter: m.starter || '',
          main: m.main || '',
          dessert: m.dessert || '',
          dietaryRequirements: m.dietaryRequirements || '',
          optedOutOfChildMenu: m.optedOutOfChildMenu === true
        }));
      } catch {
        console.warn('Could not parse family members data');
      }
    }

    // Load existing form data and merge
    const existingData = this.container.getAttribute('data-existing');
    if (existingData && existingData !== 'null') {
      try {
        const parsed = JSON.parse(existingData);

        // Merge scalar fields
        if (parsed.emailAddress) this.formData.emailAddress = parsed.emailAddress;
        if (parsed.notes) this.formData.notes = parsed.notes;

        // Merge per-guest choices from existing data (overrides family member defaults)
        if (parsed.guestChoices && Array.isArray(parsed.guestChoices)) {
          for (const existingChoice of parsed.guestChoices) {
            const match = this.formData.guestChoices.find(g => g.guestId === existingChoice.guestId);
            if (match) {
              match.attending = existingChoice.attending ?? match.attending;
              match.starter = existingChoice.starter || match.starter;
              match.main = existingChoice.main || match.main;
              match.dessert = existingChoice.dessert || match.dessert;
              match.dietaryRequirements = existingChoice.dietaryRequirements || match.dietaryRequirements;
            }
          }
        }
      } catch {
        console.warn('Could not parse existing form data');
      }
    }
  }

  // ===========================================
  // Attendance Section Visibility
  // ===========================================
  private bindAttendanceEvents(): void {
    // Bind per-guest attendance choice buttons
    const guestChoiceButtons = document.querySelectorAll('.guest-choice__btn');
    guestChoiceButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        // Use closest to handle clicks on child elements (SVG icon, path, text)
        const target = (e.target as HTMLElement).closest('.guest-choice__btn') as HTMLButtonElement;
        if (!target) return;
        const choiceContainer = target.closest('.guest-choice') as HTMLElement;
        if (!choiceContainer) return;

        const guestIndex = parseInt(choiceContainer.getAttribute('data-guest-index') || '-1', 10);
        const value = target.getAttribute('data-value') === 'true';

        if (guestIndex >= 0 && this.formData.guestChoices[guestIndex]) {
          this.formData.guestChoices[guestIndex].attending = value;
        }

        // Update button active states and aria-pressed within this guest's row
        choiceContainer.querySelectorAll('.guest-choice__btn').forEach(b => {
          b.classList.remove('guest-choice__btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        target.classList.add('guest-choice__btn--active');
        target.setAttribute('aria-pressed', 'true');
        choiceContainer.setAttribute('data-chosen', value.toString());

        this.updateAttendingSections();
        this.updateMealAccordionVisibility();
      });
    });
  }

  private allGuestsHaveChosen(): boolean {
    return this.formData.guestChoices.length > 0 &&
      this.formData.guestChoices.every(g => g.attending !== null);
  }

  private updateAttendingSections(): void {
    const attendingSections = document.querySelectorAll('.dashboard-section--attending-only');
    const declinedMessage = document.querySelector('.rsvp-declined-message') as HTMLElement;
    const saveActions = document.querySelector('.dashboard-actions') as HTMLElement;
    const allChosen = this.allGuestsHaveChosen();
    const anyAttending = this.formData.guestChoices.some(g => g.attending === true);

    // Only show additional sections once all guests have made a choice
    attendingSections.forEach((section) => {
      const sectionEl = section as HTMLElement;
      if (allChosen && anyAttending) {
        sectionEl.style.display = '';
        sectionEl.removeAttribute('aria-hidden');
        sectionEl.classList.add('dashboard-section--visible');
      } else {
        sectionEl.style.display = 'none';
        sectionEl.setAttribute('aria-hidden', 'true');
        sectionEl.classList.remove('dashboard-section--visible');
      }
    });

    // Show save button only after all guests have chosen
    if (saveActions) {
      const showSave = allChosen;
      saveActions.style.display = showSave ? '' : 'none';
      saveActions.setAttribute('aria-hidden', showSave ? 'false' : 'true');
    }

    // Show declined message only if all have chosen and none are attending
    if (declinedMessage) {
      const showDeclined = allChosen && !anyAttending;
      declinedMessage.style.display = showDeclined ? '' : 'none';
      declinedMessage.setAttribute('aria-hidden', showDeclined ? 'false' : 'true');
    }
  }

  private updateMealAccordionVisibility(): void {
    // Show/hide meal accordions based on per-guest attendance
    this.formData.guestChoices.forEach((choice, index) => {
      const accordion = document.querySelector(`.member-meal-accordion[data-member-index="${index}"]`) as HTMLElement;
      if (accordion) {
        accordion.style.display = choice.attending === true ? '' : 'none';
      }
    });

    // Close any accordion that's just been hidden — several can be open at once, so this
    // has to consider all of them rather than only the first.
    const accordions = Array.from(document.querySelectorAll('.member-meal-accordion'));
    accordions
      .filter((acc) => (acc as HTMLElement).style.display === 'none')
      .forEach((acc) => this.setAccordionOpen(acc, false));

    // If that left nothing open, open the first visible one so the section isn't just a
    // stack of closed headers.
    const anyOpen = accordions.some((acc) =>
      acc.classList.contains('member-meal-accordion--open')
    );
    if (!anyOpen) {
      const firstVisible = accordions.find((acc) => (acc as HTMLElement).style.display !== 'none');
      if (firstVisible) this.setAccordionOpen(firstVisible, true);
    }
  }

  // ===========================================
  // Input Events
  // ===========================================
  private bindInputEvents(): void {
    if (!this.form) return;

    // Text inputs
    this.form.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]').forEach((input) => {
      input.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        this.updateFormField(target.name, target.value);
        this.clearFieldError(target.name);
      });
    });

    // Textareas — per-guest fields (dietary requirements) are named
    // "guestChoices[n].field" and belong on the guest, not the top-level form.
    this.form.querySelectorAll('textarea').forEach((textarea) => {
      textarea.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        if (target.name.startsWith('guestChoices[')) {
          this.updateMemberMealField(target.name, target.value);
        } else {
          this.updateFormField(target.name, target.value);
        }
      });
    });

    // Radio buttons (per-member meal selections)
    this.form.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        this.updateMemberMealField(target.name, target.value);
      });
    });

    // Checkboxes — as with textareas, per-guest fields are named "guestChoices[n].field"
    // and belong on the guest rather than the top-level form.
    this.form.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        if (target.name.startsWith('guestChoices[')) {
          this.updateMemberMealField(target.name, target.checked);
          this.updateChildMenuVisibility(target);
        } else {
          this.updateFormField(target.name, target.checked);
        }
      });
    });
  }

  private updateFormField(name: string, value: string | boolean): void {
    switch (name) {
      case 'emailAddress':
        this.formData.emailAddress = value as string;
        break;
      case 'notes':
        this.formData.notes = value as string;
        break;
    }
  }

  /**
   * Shows the "Options chosen, thank you" acknowledgement once a guest has picked every
   * course that's actually on offer to them. Guests with nothing to choose — under-ones,
   * and children opted out of the children's menu — get their own note, so an unanswered
   * guest is the only one left without a tick.
   */
  private updateMealStatus(memberIndex: number): void {
    const accordion = document.querySelector(
      `.member-meal-accordion[data-member-index="${memberIndex}"]`
    );
    if (!accordion) return;

    const status = accordion.querySelector('[data-meal-status]') as HTMLElement | null;
    const statusText = accordion.querySelector('[data-meal-status-text]');
    const choice = this.formData.guestChoices[memberIndex];
    if (!status || !statusText || !choice) return;

    // Which courses this guest is actually offered — the children's menu may not have all
    // three, so requiring all of them would leave the tick permanently out of reach.
    const required = (accordion.getAttribute('data-required-courses') || '')
      .split(',')
      .filter(Boolean) as Array<'starter' | 'main' | 'dessert'>;

    let text = '';
    if (accordion.getAttribute('data-no-menu') === 'true') {
      text = 'No menu needed';
    } else if (choice.optedOutOfChildMenu) {
      text = "No children's menu";
    } else if (required.length > 0 && required.every((course) => !!choice[course])) {
      text = 'Options chosen, thank you';
    }

    statusText.textContent = text;
    status.classList.toggle('member-meal-accordion__status--done', text !== '');
  }

  /**
   * Hides a child's course pickers while they're opted out of the children's menu, and
   * clears any radios they'd already selected so the UI matches the submitted data.
   */
  private updateChildMenuVisibility(toggle: HTMLInputElement): void {
    const accordion = toggle.closest('.member-meal-accordion');
    const courses = accordion?.querySelector('[data-meal-courses]') as HTMLElement | null;
    if (!courses) return;

    courses.style.display = toggle.checked ? 'none' : '';

    if (toggle.checked) {
      courses.querySelectorAll('input[type="radio"]').forEach((radio) => {
        (radio as HTMLInputElement).checked = false;
      });
    }
  }

  private updateMemberMealField(name: string, value: string | boolean): void {
    // Parse field name like "guestChoices[0].starter"
    const match = name.match(/guestChoices\[(\d+)\]\.(\w+)/);
    if (!match) return;

    const memberIndex = parseInt(match[1], 10);
    const fieldName = match[2];

    // Ensure member entry exists
    if (!this.formData.guestChoices[memberIndex]) {
      const accordion = document.querySelector(`.member-meal-accordion[data-member-index="${memberIndex}"]`);
      const guestId = accordion?.getAttribute('data-guest-id') || '';

      this.formData.guestChoices[memberIndex] = {
        guestId: guestId,
        memberIndex: memberIndex,
        memberName: '',
        attending: false,
        starter: '',
        main: '',
        dessert: '',
        dietaryRequirements: '',
        optedOutOfChildMenu: false
      };
    }

    const choice = this.formData.guestChoices[memberIndex];

    if (fieldName === 'optedOutOfChildMenu') {
      choice.optedOutOfChildMenu = value === true;

      // Courses are meaningless once a child is off the menu — drop anything already
      // picked so a stale choice isn't submitted alongside the opt-out.
      if (choice.optedOutOfChildMenu) {
        choice.starter = '';
        choice.main = '';
        choice.dessert = '';
      }
      this.updateMealStatus(memberIndex);
      return;
    }

    if (
      fieldName === 'starter' ||
      fieldName === 'main' ||
      fieldName === 'dessert' ||
      fieldName === 'dietaryRequirements'
    ) {
      choice[fieldName] = value as string;
    }

    this.updateMealStatus(memberIndex);
  }

  // ===========================================
  // Accordion Functionality
  // ===========================================
  private bindAccordionEvents(): void {
    // Each accordion toggles independently — with a family of several guests it's useful
    // to have more than one set of choices open at a time to compare them.
    document.querySelectorAll('.member-meal-accordion__header').forEach((header) => {
      header.addEventListener('click', () => {
        const accordion = header.closest('.member-meal-accordion');
        if (!accordion) return;

        this.setAccordionOpen(
          accordion,
          !accordion.classList.contains('member-meal-accordion--open')
        );
      });
    });

    // "Close X's choices" link at the foot of each accordion.
    document.querySelectorAll('[data-collapse-accordion]').forEach((link) => {
      link.addEventListener('click', () => {
        const accordion = link.closest('.member-meal-accordion');
        if (!accordion) return;

        this.setAccordionOpen(accordion, false);

        // Collapsing from the foot of a long list would otherwise leave the page scrolled
        // into the space the accordion just vacated, so follow the header back up.
        const header = accordion.querySelector('.member-meal-accordion__header');
        if (header) {
          const top = header.getBoundingClientRect().top + window.scrollY - HASH_SCROLL_OFFSET;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      });
    });

    // Open the first accordion by default
    const firstAccordion = document.querySelector('.member-meal-accordion');
    if (firstAccordion) this.setAccordionOpen(firstAccordion, true);
  }

  private setAccordionOpen(accordion: Element, open: boolean): void {
    accordion.classList.toggle('member-meal-accordion--open', open);
    accordion
      .querySelector('.member-meal-accordion__header')
      ?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // ===========================================
  // Validation
  // ===========================================
  private validate(): ValidationResult {
    const errors: Record<string, string> = {};
    const anyAttending = this.formData.guestChoices.some(g => g.attending === true);

    // If any guest is attending, email is required
    if (anyAttending) {
      if (!this.formData.emailAddress || !this.formData.emailAddress.trim()) {
        errors['emailAddress'] = 'Please provide your email address';
      } else if (!this.isValidEmail(this.formData.emailAddress)) {
        errors['emailAddress'] = 'Please enter a valid email address';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private showValidationErrors(errors: Record<string, string>): void {
    Object.entries(errors).forEach(([field, message]) => {
      const errorEl = document.querySelector(`[data-error="${field}"]`);
      if (errorEl) {
        errorEl.textContent = message;
      }

      const input = this.form?.querySelector(`[name="${field}"]`);
      input?.classList.add('form-input--error');
    });

    // Scroll to first error. Same reason as scrollToHashTarget for avoiding
    // scrollIntoView here — .rsvp-container is overflow:hidden and would be scrolled
    // instead of the page, shunting the whole card out of alignment.
    const firstErrorField = Object.keys(errors)[0];
    const firstErrorInput = this.form?.querySelector(`[name="${firstErrorField}"]`);
    if (firstErrorInput) {
      const rect = firstErrorInput.getBoundingClientRect();
      const top = rect.top + window.scrollY - (window.innerHeight - rect.height) / 2;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }
  }

  private clearValidationErrors(): void {
    document.querySelectorAll('[data-error]').forEach((el) => {
      el.textContent = '';
    });

    document.querySelectorAll('.form-input--error').forEach((el) => {
      el.classList.remove('form-input--error');
    });
  }

  private clearFieldError(fieldName: string): void {
    const errorEl = document.querySelector(`[data-error="${fieldName}"]`);
    if (errorEl) {
      errorEl.textContent = '';
    }

    const input = this.form?.querySelector(`[name="${fieldName}"]`);
    input?.classList.remove('form-input--error');
  }

  // ===========================================
  // Form Submission
  // ===========================================
  private bindFormSubmit(): void {
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitForm();
    });
  }

  private async submitForm(): Promise<void> {
    // The RSVP and meal choice windows close independently — allow the save while
    // either is still open, not just while the RSVP deadline holds.
    if (this.isSubmitting || !this.canSave) return;

    // Clear previous errors
    this.clearValidationErrors();

    // Validate
    const validation = this.validate();
    if (!validation.isValid) {
      this.showValidationErrors(validation.errors);
      return;
    }

    this.isSubmitting = true;
    this.setLoadingState(true);
    this.updateSaveStatus('Saving...');

    try {
      const response = await this.sendRsvpData();

      if (response.success) {
        this.updateSaveStatus('Saved successfully!', 'success');
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.updateSaveStatus('');
        }, 3000);
      } else {
        this.updateSaveStatus(response.message || 'Failed to save. Please try again.', 'error');
        if (response.errors) {
          this.handleServerErrors(response.errors);
        }
      }
    } catch (error: any) {
      this.updateSaveStatus(error.message || 'Unable to save. Please check your connection and try again.', 'error');
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
    }
  }

  private async sendRsvpData(): Promise<ApiResponse> {
    const anyAttending = this.formData.guestChoices.some(g => g.attending === true);

    // Coerce null attending values to false for submission
    const guestChoices = this.formData.guestChoices.map(g => ({
      ...g,
      attending: g.attending === true
    }));

    const payload: Record<string, unknown> = {
      uniqueCode: this.formData.uniqueCode,
      guestChoices: guestChoices
    };

    // Only include additional fields if any guest is attending
    if (anyAttending) {
      payload.emailAddress = this.formData.emailAddress;
      payload.notes = this.formData.notes;
    }

    // Always include guest names for confirmation email
    const guestNamesInput = this.form?.querySelector('input[name="guestNames"]') as HTMLInputElement;
    if (guestNamesInput) {
      payload.guestNames = guestNamesInput.value;
    }

    const response = await fetch('/api/rsvp/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'RequestVerificationToken': this.getAntiForgeryToken()
      },
      body: JSON.stringify(payload)
    });

    var responseJson = await response.json();
    if (!response.ok) {
      throw new Error(responseJson.message || `HTTP error! status: ${response.status}`);
    }

    return responseJson;
  }

  private getAntiForgeryToken(): string {
    const token = document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement;
    return token?.value || '';
  }

  private setLoadingState(loading: boolean): void {
    const submitBtn = document.getElementById('btn-save') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = loading;
      submitBtn.classList.toggle('btn--loading', loading);
    }
  }

  private updateSaveStatus(message: string, type?: 'success' | 'error'): void {
    const statusEl = document.getElementById('save-status');
    if (statusEl) {
      statusEl.textContent = message;
      statusEl.className = 'dashboard-actions__hint';
      if (type) {
        statusEl.classList.add(`dashboard-actions__hint--${type}`);
      }
    }
  }

  private handleServerErrors(errors: Record<string, string[]>): void {
    Object.entries(errors).forEach(([field, messages]) => {
      const errorEl = document.querySelector(`[data-error="${field}"]`);
      if (errorEl && messages.length > 0) {
        errorEl.textContent = messages[0];
      }

      const input = this.form?.querySelector(`[name="${field}"]`);
      input?.classList.add('form-input--error');
    });
  }
}

// ===========================================
// Export initializer (called from main.ts)
// ===========================================
export function initRsvpForm(): void {
  const dashboard = new RsvpDashboardManager();
  dashboard.init();
}
